import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home, LayoutGrid, Monitor, Settings, HelpCircle, LogOut,
  Bell, ChevronDown, MoreVertical, Send, X, Plus, Power, Trash2,
  Activity, ShieldCheck, Loader2, Star,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { useAuth } from '../helpers/useAuth';
import { toast } from 'sonner';
import superjson from 'superjson';
import styles from './portal.module.css';

// ─── Types ───
interface Desktop {
  id: string;
  name: string;
  hostname: string | null;
  ip_address: string | null;
  rdp_port: number;
  status: string;
  os_type: string;
  vm_size: string | null;
  cpu_percent: number;
  ram_percent: number;
  latency_ms: number;
  last_connected_at: string | null;
}

interface PortalApp {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
  launch_url: string | null;
  desktop_id: string | null;
  is_global: number;
}

// ─── Sidebar icons ───
const NAV_ITEMS = [
  { icon: Home, label: 'Home', active: true },
  { icon: LayoutGrid, label: 'Apps' },
  { icon: Monitor, label: 'Desktops' },
  { icon: Settings, label: 'Settings' },
];

const NAV_BOTTOM = [
  { icon: HelpCircle, label: 'Help' },
  { icon: LogOut, label: 'Sign Out' },
];

// ─── Hooks ───
function usePortalDesktops() {
  return useQuery({
    queryKey: ['portal', 'desktops'],
    queryFn: async (): Promise<Desktop[]> => {
      const res = await fetch('/_api/portal/desktops', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list' }),
      });
      if (!res.ok) throw new Error('Failed to load desktops');
      const data = superjson.parse<{ desktops: Desktop[] }>(await res.text());
      return data.desktops || [];
    },
    staleTime: 5000,
    refetchInterval: 8000,
  });
}

function usePortalApps() {
  return useQuery({
    queryKey: ['portal', 'apps'],
    queryFn: async (): Promise<PortalApp[]> => {
      const res = await fetch('/_api/portal/apps', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list' }),
      });
      if (!res.ok) throw new Error('Failed to load apps');
      const data = superjson.parse<{ apps: PortalApp[] }>(await res.text());
      return data.apps || [];
    },
    staleTime: 10000,
    refetchInterval: 15000,
  });
}

// ─── RDP Session Component — connects via cloud connector relay ───
function RdpSession({ desktop, onClose }: { desktop: Desktop; onClose: () => void }) {
  const rdpHost = desktop.hostname || desktop.ip_address || '';
  const rdpPort = desktop.rdp_port || 3389;
  const [status, setStatus] = useState<'connecting' | 'connected' | 'error' | 'no_connector'>('connecting');
  const [errorMsg, setErrorMsg] = useState('');
  const wsRef = useRef<WebSocket | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    // Connect to the RDP relay Durable Object via WebSocket
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${proto}//${location.host}/_api/portal/rdp-relay/${desktop.id}?role=client&host=${encodeURIComponent(rdpHost)}&port=${rdpPort}&desktopId=${desktop.id}&userId=admin`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      setStatus('connecting');
    };

    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        // Binary RDP frame from connector — render via IronRDP WASM
        // TODO: Feed to IronRDP WASM decoder when loaded
        // For now, this is where the RDP pixel data arrives
        return;
      }
      // Control message
      try {
        const msg = JSON.parse(event.data);
        switch (msg.type) {
          case 'connecting':
            setStatus('connecting');
            break;
          case 'rdp_connected':
            setStatus('connected');
            break;
          case 'error':
            if (msg.code === 'NO_CONNECTOR') {
              setStatus('no_connector');
              setErrorMsg('Cloud connector is not connected. Ensure the connector agent is running on your network.');
            } else {
              setStatus('error');
              setErrorMsg(msg.message || 'Connection failed');
            }
            break;
          case 'connector_disconnected':
            setStatus('no_connector');
            setErrorMsg('Cloud connector disconnected.');
            break;
          case 'vm_ip_resolved':
            // Connector resolved the private IP — could update UI
            break;
        }
      } catch {}
    };

    ws.onerror = () => {
      setStatus('error');
      setErrorMsg('WebSocket connection failed');
    };

    ws.onclose = () => {
      if (status === 'connecting') {
        setStatus('error');
        setErrorMsg('Connection closed unexpectedly');
      }
    };

    return () => {
      try { ws.close(); } catch {}
    };
  }, [desktop.id, rdpHost, rdpPort]);

  return (
    <div className={styles.rdpOverlay}>
      <div className={styles.rdpHeader}>
        <Monitor size={16} style={{ color: status === 'connected' ? '#00ff88' : status === 'error' || status === 'no_connector' ? '#ef4444' : '#f59e0b' }} />
        <span>{desktop.name}</span>
        <span style={{ fontSize: '0.72rem', color: '#6a7a76' }}>
          {rdpHost ? `${rdpHost}:${rdpPort}` : 'Resolving via connector...'}
        </span>
        <span style={{ fontSize: '0.68rem', color: status === 'connected' ? '#00ff88' : '#f59e0b', marginLeft: 'auto' }}>
          {status === 'connected' ? 'CONNECTED' : status === 'no_connector' ? 'NO CONNECTOR' : status === 'error' ? 'ERROR' : 'CONNECTING'}
        </span>
        <button onClick={onClose} className={styles.rdpClose}>
          <X size={16} />
        </button>
      </div>
      <div className={styles.rdpCanvas}>
        <canvas
          ref={canvasRef}
          id={`rdp-canvas-${desktop.id}`}
          style={{ width: '100%', height: '100%', background: '#0a0f0d' }}
        />
        {status !== 'connected' && (
          <div className={styles.rdpConnecting}>
            {status === 'connecting' && (
              <>
                <Loader2 size={24} className={styles.spin} />
                <span>Connecting to {desktop.name} via cloud connector...</span>
                <span style={{ fontSize: '0.72rem', color: '#6a7a76', marginTop: 4 }}>
                  {rdpHost ? `Target: ${rdpHost}:${rdpPort}` : 'Resolving private IP through connector'}
                </span>
              </>
            )}
            {status === 'no_connector' && (
              <>
                <Monitor size={24} style={{ color: '#ef4444' }} />
                <span style={{ color: '#ef4444' }}>Cloud Connector Not Available</span>
                <span style={{ fontSize: '0.78rem', color: '#6a7a76', marginTop: 4, maxWidth: 400, textAlign: 'center' }}>
                  {errorMsg}
                </span>
                <span style={{ fontSize: '0.72rem', color: '#555', marginTop: 8 }}>
                  The connector agent must be running inside your network to relay RDP connections to private VMs.
                </span>
              </>
            )}
            {status === 'error' && (
              <>
                <X size={24} style={{ color: '#ef4444' }} />
                <span style={{ color: '#ef4444' }}>{errorMsg || 'Connection failed'}</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Add Desktop Dialog ───
function AddDesktopForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [hostname, setHostname] = useState('');
  const [ip, setIp] = useState('');
  const [port, setPort] = useState('3389');
  const [status, setStatus] = useState('running');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!hostname.trim() && !ip.trim()) { toast.error('Hostname or IP is required'); return; }
    setSaving(true);
    try {
      await fetch('/_api/portal/desktops', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          name: name.trim(),
          hostname: hostname.trim() || null,
          ipAddress: ip.trim() || null,
          rdpPort: parseInt(port) || 3389,
          status,
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['portal', 'desktops'] });
      toast.success(`Desktop "${name}" added`);
      onClose();
    } catch (e: any) {
      toast.error(e.message || 'Failed to add desktop');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.addForm}>
      <div className={styles.addFormTitle}>Add Desktop</div>
      <input placeholder="Display name (e.g. WIN-DEV-03)" value={name} onChange={e => setName(e.target.value)} className={styles.chatInput} />
      <input placeholder="Hostname" value={hostname} onChange={e => setHostname(e.target.value)} className={styles.chatInput} />
      <input placeholder="IP Address" value={ip} onChange={e => setIp(e.target.value)} className={styles.chatInput} />
      <input placeholder="RDP Port (3389)" value={port} onChange={e => setPort(e.target.value)} className={styles.chatInput} />
      <select value={status} onChange={e => setStatus(e.target.value)} className={styles.chatInput} style={{ appearance: 'auto' }}>
        <option value="running">Running</option>
        <option value="shutdown">Shutdown</option>
        <option value="maintenance">Maintenance</option>
      </select>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onClose} className={styles.chatSuggestion}>Cancel</button>
        <button onClick={handleSave} disabled={saving} className={styles.chatSendBtn} style={{ width: 'auto', padding: '8px 16px', fontSize: '0.82rem' }}>
          {saving ? <Loader2 size={14} className={styles.spin} /> : 'Add'}
        </button>
      </div>
    </div>
  );
}

// ─── Main ───
export default function PortalPage() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAdmin = authState.type === 'authenticated' && authState.user?.role === 'admin';
  const userEmail = authState.type === 'authenticated' ? authState.user?.email : '';
  const userName = authState.type === 'authenticated' ? authState.user?.displayName : '';

  const { data: desktops = [], isLoading } = usePortalDesktops();
  const { data: apps = [] } = usePortalApps();
  const [activeRdp, setActiveRdp] = useState<Desktop | null>(null);
  const [showAddDesktop, setShowAddDesktop] = useState(false);
  const [activeTab, setActiveTab] = useState('Home');

  // Favorites — persist to localStorage
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('portal_favorites');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });
  const toggleFavorite = useCallback((id: string) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      localStorage.setItem('portal_favorites', JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Chat state
  const [chatOpen, setChatOpen] = useState(true);
  const [messages, setMessages] = useState<Array<{ role: 'bot' | 'user'; text: string; actions?: string[] }>>([
    { role: 'bot', text: `Welcome${userName ? `, ${userName}` : ''}. I can manage your desktops, troubleshoot sessions, and launch apps. What do you need?` },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const sendChat = useCallback(async (text: string) => {
    if (!text.trim() || chatLoading) return;
    const userMsg = text.trim();
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setChatInput('');
    setChatLoading(true);

    try {
      // Send to real AI via the session chat endpoint with portal context
      const res = await fetch('/_api/ai/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMsg,
          systemContext: `You are the VirtualExpanse Workspace Assistant. You help users manage their virtual desktops, troubleshoot RDP sessions, check resource usage, and launch applications. Be concise and technical. The user has these desktops: ${desktops.map(d => `${d.name} (${d.hostname || d.ip_address}, status: ${d.status}, CPU: ${d.cpu_percent}%, RAM: ${d.ram_percent}%)`).join('; ') || 'none configured'}. User: ${userEmail}`,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const botText = data.response || data.message || data.content || 'No response received.';
        setMessages(prev => [...prev, { role: 'bot', text: botText }]);
      } else {
        // Fallback to Cloudflare AI directly
        const cfRes = await fetch('/_api/tts/cloudflare', { method: 'HEAD' }).catch(() => null);
        setMessages(prev => [...prev, { role: 'bot', text: 'AI assistant is not configured yet. Please set up an AI provider in your session settings.' }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'bot', text: 'Connection error. Please try again.' }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, [chatLoading, desktops, userEmail]);

  const updateDesktop = useCallback(async (id: string, updates: Record<string, any>) => {
    try {
      await fetch('/_api/portal/desktops', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update', id, ...updates }),
      });
      queryClient.invalidateQueries({ queryKey: ['portal', 'desktops'] });
    } catch (e: any) {
      toast.error(e.message || 'Failed to update desktop');
    }
  }, [queryClient]);

  const togglePower = useCallback(async (desktop: Desktop) => {
    const action = desktop.status === 'running' ? 'stop' : 'start';
    const label = action === 'start' ? 'Starting' : 'Stopping';
    // Immediately set transitioning state in UI
    await updateDesktop(desktop.id, { status: action === 'start' ? 'starting' : 'deallocating' });
    toast.info(`${label} ${desktop.name}...`);
    try {
      const res = await fetch('/_api/portal/vm-action', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ desktopId: desktop.id, action }),
      });
      const data = await res.json() as any;
      if (!res.ok) {
        toast.error(data.error || `Failed to ${action} VM`);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['portal', 'desktops'] });
      const ipNote = data.ip ? ` (${data.ip})` : '';
      toast.success(`${desktop.name} → ${data.status}${ipNote}${data.provider === 'local' ? ' (local only)' : ''}`);
    } catch (err: any) {
      toast.error(err.message || `Failed to ${action} VM`);
      queryClient.invalidateQueries({ queryKey: ['portal', 'desktops'] });
    }
  }, [updateDesktop, queryClient]);

  const handleDeleteDesktop = useCallback(async (id: string) => {
    if (!confirm('Remove this desktop?')) return;
    try {
      await fetch('/_api/portal/desktops', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', id }),
      });
      queryClient.invalidateQueries({ queryKey: ['portal', 'desktops'] });
      toast.success('Desktop removed');
    } catch (e: any) {
      toast.error(e.message || 'Failed to remove desktop');
    }
  }, [queryClient]);

  // ─── RDP Session active ───
  if (activeRdp) {
    return (
      <div className={styles.portal}>
        <RdpSession desktop={activeRdp} onClose={() => setActiveRdp(null)} />
      </div>
    );
  }

  return (
    <div className={styles.portal}>
      {/* Top Bar */}
      <div className={styles.topBar}>
        <div className={styles.logo}>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <rect width="28" height="28" rx="6" fill="#00ff88" fillOpacity="0.12" stroke="#00ff88" strokeOpacity="0.3" />
            <path d="M8 20c0-6 4-12 12-12" stroke="#00ff88" strokeWidth="2.5" strokeLinecap="round" fill="none" />
            <path d="M10 20c0-4.5 3-9 9-9" stroke="#00ff88" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.6" />
          </svg>
          <span>VIRTUAL<span className={styles.logoAccent}>EXPANSE</span><span style={{ opacity: 0.4, fontWeight: 400, fontSize: '0.75em' }}>.io</span></span>
        </div>
        <div className={styles.topBarRight}>
          <Bell size={18} style={{ color: '#888', cursor: 'pointer' }} />
          <div className={styles.userPill}>
            <ShieldCheck size={14} style={{ color: '#00ff88' }} />
            {userEmail}
            <ChevronDown size={14} style={{ color: '#888' }} />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Sidebar */}
        <nav className={styles.sidebar}>
          <div className={styles.sidebarTop}>
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <button key={item.label} className={`${styles.sidebarBtn} ${activeTab === item.label ? styles.sidebarBtnActive : ''}`} title={item.label} onClick={() => setActiveTab(item.label)}>
                  <Icon size={18} />
                </button>
              );
            })}
          </div>
          <div className={styles.sidebarBottom}>
            {isAdmin && (
              <button className={`${styles.sidebarBtn} ${styles.adminShield}`} title="Admin Management" onClick={() => navigate('/dashboard')}>
                <ShieldCheck size={18} />
              </button>
            )}
            {NAV_BOTTOM.map((item) => {
              const Icon = item.icon;
              return (
                <button key={item.label} className={styles.sidebarBtn} title={item.label}>
                  <Icon size={18} />
                </button>
              );
            })}
          </div>
        </nav>

        {/* Main Content */}
        <div className={styles.content}>
          <div>
            <h1 className={styles.pageTitle}>{activeTab === 'Apps' ? 'Applications' : activeTab === 'Desktops' ? 'Desktops' : 'Workspace'}</h1>
            <p className={styles.pageSubtitle}>VirtualExpanse.io</p>
          </div>

          {/* Favorites */}
          {(activeTab === 'Home') && favorites.size > 0 && (
            <section>
              <div className={styles.sectionTitle} style={{ marginBottom: 12 }}>Favorites</div>
              <div className={styles.cloudGrid}>
                {desktops.filter(d => favorites.has(d.id)).map(d => (
                  <div key={d.id} className={styles.desktopCard} onClick={() => setActiveRdp(d)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Monitor size={18} style={{ color: '#3b82f6' }} />
                      <span className={styles.desktopName} style={{ marginBottom: 0 }}>{d.name}</span>
                      <Star size={14} fill="#f59e0b" stroke="#f59e0b" style={{ marginLeft: 'auto', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); toggleFavorite(d.id); }} />
                    </div>
                  </div>
                ))}
                {apps.filter(a => favorites.has(a.id)).map(a => (
                  <div key={a.id} className={styles.desktopCard} onClick={() => {
                    if (a.desktop_id) {
                      const desktop = desktops.find(d => d.id === a.desktop_id);
                      if (desktop) setActiveRdp(desktop);
                    }
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <LayoutGrid size={18} style={{ color: a.color || '#3b82f6' }} />
                      <span className={styles.desktopName} style={{ marginBottom: 0 }}>{a.name}</span>
                      <Star size={14} fill="#f59e0b" stroke="#f59e0b" style={{ marginLeft: 'auto', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); toggleFavorite(a.id); }} />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Apps */}
          {(activeTab === 'Home' || activeTab === 'Apps') && apps.length > 0 && (
            <section>
              <div className={styles.sectionTitle} style={{ marginBottom: 12 }}>Applications</div>
              <div className={styles.cloudGrid}>
                {apps.map(a => {
                  const linkedDesktop = a.desktop_id ? desktops.find(d => d.id === a.desktop_id) : null;
                  return (
                    <div
                      key={a.id}
                      className={styles.desktopCard}
                      onClick={() => {
                        if (linkedDesktop) {
                          setActiveRdp(linkedDesktop);
                        } else if (a.launch_url) {
                          window.open(a.launch_url, '_blank');
                        } else {
                          toast.error('No desktop or launch URL configured for this app');
                        }
                      }}
                    >
                      <div className={styles.desktopTop}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 8,
                          background: a.color || 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '1.1rem',
                        }}>
                          {a.icon || a.name.charAt(0).toUpperCase()}
                        </div>
                        <Star
                          size={14}
                          fill={favorites.has(a.id) ? '#f59e0b' : 'none'}
                          stroke={favorites.has(a.id) ? '#f59e0b' : '#555'}
                          style={{ marginLeft: 'auto', cursor: 'pointer' }}
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(a.id); }}
                        />
                      </div>
                      <div className={styles.desktopName}>{a.name}</div>
                      <div style={{ fontSize: '0.7rem', color: '#6a7a76' }}>
                        {linkedDesktop ? `via ${linkedDesktop.name}` : a.launch_url ? 'Web app' : 'RemoteApp'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Desktops */}
          {(activeTab === 'Home' || activeTab === 'Desktops') && (
          <section>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div className={styles.sectionTitle} style={{ marginBottom: 0 }}>Desktops</div>
              <button
                onClick={() => setShowAddDesktop(true)}
                style={{ background: 'none', border: '1px solid var(--g-border, rgba(0,255,136,0.18))', borderRadius: 6, color: '#00ff88', cursor: 'pointer', padding: '4px 10px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <Plus size={12} /> Add
              </button>
            </div>

            {showAddDesktop && <AddDesktopForm onClose={() => setShowAddDesktop(false)} />}

            {isLoading ? (
              <div style={{ color: '#6a7a76', fontSize: '0.85rem', padding: 20 }}>
                <Loader2 size={16} className={styles.spin} style={{ display: 'inline', marginRight: 8 }} />
                Loading desktops...
              </div>
            ) : desktops.length === 0 ? (
              <div style={{ color: '#6a7a76', fontSize: '0.85rem', padding: '20px 0' }}>
                No desktops configured. Click "Add" to register your first virtual desktop.
              </div>
            ) : (
              <div className={styles.cloudGrid}>
                {desktops.map((d) => {
                  const isRunning = d.status === 'running';
                  const isTransitioning = ['starting', 'stopping', 'deallocating', 'draining'].includes(d.status);
                  const statusColor = isRunning ? '#00ff88' : isTransitioning ? '#f59e0b' : d.status === 'maintenance' || d.status === 'draining' ? '#f59e0b' : '#555';
                  const statusLabel = d.status.charAt(0).toUpperCase() + d.status.slice(1);

                  return (
                    <div
                      key={d.id}
                      className={styles.desktopCard}
                      onClick={() => {
                        setActiveRdp(d);
                      }}
                    >
                      <div className={styles.desktopTop}>
                        <div style={{ position: 'relative', display: 'inline-flex' }}>
                          <Monitor size={24} style={{ color: isRunning ? '#3b82f6' : '#555', opacity: isRunning ? 1 : 0.5 }} />
                          {isRunning && (
                            <div style={{
                              position: 'absolute',
                              top: 2, left: 3, width: 18, height: 12,
                              borderRadius: 2,
                              background: 'linear-gradient(180deg, #60a5fa 0%, #3b82f6 60%, #2563eb 100%)',
                              opacity: 0.35,
                              boxShadow: '0 0 8px rgba(59,130,246,0.4)',
                              pointerEvents: 'none',
                            }} />
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                          <Star
                            size={13}
                            fill={favorites.has(d.id) ? '#f59e0b' : 'none'}
                            stroke={favorites.has(d.id) ? '#f59e0b' : '#555'}
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => { e.stopPropagation(); toggleFavorite(d.id); }}
                          />
                          <button
                            className={styles.powerBtn}
                            data-on={isRunning ? 'true' : 'false'}
                            onClick={(e) => { e.stopPropagation(); togglePower(d); }}
                            title={isRunning ? 'Shut down' : 'Start'}
                            disabled={isTransitioning}
                          >
                            {isTransitioning
                              ? <Loader2 size={13} className={styles.spin} />
                              : <Power size={13} />
                            }
                          </button>
                          <button
                            className={styles.desktopMenu}
                            onClick={(e) => { e.stopPropagation(); handleDeleteDesktop(d.id); }}
                            title="Remove"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      <div className={styles.desktopName}>{d.name}</div>
                      <div style={{ fontSize: '0.7rem', color: '#6a7a76', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor, boxShadow: isRunning ? `0 0 6px ${statusColor}` : 'none', flexShrink: 0 }} />
                        {statusLabel} · {d.hostname || d.ip_address || 'No host'}
                      </div>
                      <div className={styles.healthBar}>
                        <div className={styles.healthFill} style={{ width: `${isRunning ? Math.max(d.cpu_percent, 5) : 0}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          )}

          {/* Session Monitor */}
          {desktops.length > 0 && (
            <section>
              <div className={styles.sectionTitle}>Session Monitor</div>
              <div className={styles.monitorBar}>
                <div className={styles.monitorLabel}>Aggregate telemetry across {desktops.length} desktop{desktops.length !== 1 ? 's' : ''}</div>
                <div className={styles.monitorStats}>
                  <div className={styles.monitorStat}>
                    <span className={styles.monitorStatLabel}>Avg Latency</span>
                    <span className={styles.monitorStatValue}>{desktops.length ? Math.round(desktops.reduce((a, d) => a + d.latency_ms, 0) / desktops.length) : 0}ms</span>
                    <Activity size={16} style={{ color: '#00ff88' }} />
                  </div>
                  <div className={styles.monitorStat}>
                    <span className={styles.monitorStatLabel}>Avg CPU</span>
                    <span className={styles.monitorStatValue}>{desktops.length ? Math.round(desktops.reduce((a, d) => a + d.cpu_percent, 0) / desktops.length) : 0}%</span>
                    <div className={styles.miniBar}><div className={styles.miniFill} style={{ width: `${desktops.length ? Math.round(desktops.reduce((a, d) => a + d.cpu_percent, 0) / desktops.length) : 0}%` }} /></div>
                  </div>
                  <div className={styles.monitorStat}>
                    <span className={styles.monitorStatLabel}>Avg RAM</span>
                    <span className={styles.monitorStatValue}>{desktops.length ? Math.round(desktops.reduce((a, d) => a + d.ram_percent, 0) / desktops.length) : 0}%</span>
                    <div className={styles.miniBar}><div className={styles.miniFill} style={{ width: `${desktops.length ? Math.round(desktops.reduce((a, d) => a + d.ram_percent, 0) / desktops.length) : 0}%` }} /></div>
                  </div>
                  <div className={styles.monitorStat}>
                    <span className={styles.monitorStatLabel}>Online</span>
                    <span className={styles.monitorStatValue} style={{ color: '#00ff88' }}>{desktops.filter(d => d.status === 'running').length}/{desktops.length}</span>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* AI Chat Panel */}
        {chatOpen && (
          <div className={styles.chatPanel}>
            <div className={styles.chatHeader}>
              <div className={styles.chatBotIcon}>
                <svg width="18" height="18" viewBox="0 0 28 28" fill="none">
                  <path d="M8 20c0-6 4-12 12-12" stroke="#00ff88" strokeWidth="2.5" strokeLinecap="round" />
                  <path d="M10 20c0-4.5 3-9 9-9" stroke="#00ff88" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
                </svg>
              </div>
              <div>
                <div className={styles.chatBotName}>EXPANSE ASSISTANT</div>
                <div className={styles.chatBotSub}><span style={{ color: '#00ff88' }}>ONLINE</span> | Context-Aware</div>
              </div>
              <button onClick={() => setChatOpen(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <div className={styles.chatMessages}>
              {messages.map((msg, i) => (
                <div key={i}>
                  <div className={`${styles.chatMsg} ${msg.role === 'bot' ? styles.chatMsgBot : styles.chatMsgUser}`}>
                    {msg.text.split('\n').map((line, j) => (
                      <React.Fragment key={j}>
                        {j > 0 && <br />}
                        {line}
                      </React.Fragment>
                    ))}
                  </div>
                  {msg.role === 'bot' && msg.actions && msg.actions.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                      {msg.actions.map((a) => (
                        <button key={a} className={styles.chatSuggestion}>[{a}]</button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {chatLoading && (
                <div className={`${styles.chatMsg} ${styles.chatMsgBot}`}>
                  <Loader2 size={14} className={styles.spin} style={{ display: 'inline', marginRight: 6 }} />
                  Thinking...
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className={styles.chatSuggestions}>
              {['Fix my slow session', 'Show desktop status', 'Why can\'t I connect?'].map((s) => (
                <button key={s} className={styles.chatSuggestion} onClick={() => sendChat(s)}>{s}</button>
              ))}
            </div>

            <div className={styles.chatInputArea}>
              <input
                className={styles.chatInput}
                placeholder="Ask about your workspace..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') sendChat(chatInput); }}
                disabled={chatLoading}
              />
              <button className={styles.chatSendBtn} onClick={() => sendChat(chatInput)} disabled={chatLoading}>
                <Send size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
